import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { DoneAuditingComponent } from '@components/done-auditing/done-auditing.component';
import * as ActionCable from '@rails/actioncable';
import { AttachePayload } from '@services/attache';
import { Cable } from '@services/cable';
import { OverlayService } from '@services/overlay';
import { Subscription } from 'rxjs';
import { RetakeDocumentPayload } from 'src/app/interfaces/retake-document-payload.interface';
import { CommonModule } from '@angular/common';
import { CaptionedImageComponent } from '../captioned-image/captioned-image.component';
import { ListRetakeRequestsComponent } from '../list-retake-requests/list-retake-requests.component';
import { TranslateModule } from '@ngx-translate/core';
import { Identification, Session, SessionServicesEnum, VerificationServiceEnum } from 'src/app/queries/generated';
import { VerificationsService } from '@services/verifications.service';
import { RetakeRequestsService } from '@services/retake-requests.service';
import { AttacheOptions } from '@models/attache-options';
import { SessionAttendanceStatusComponent } from '@components/session-attendance-status/session-attendance-status.component';

interface SessionChannelData {
  freshFacePhoto?: AttachePayload;
  freshIdPhoto?: AttachePayload;
}

@Component({
  selector: 'lv-agent-verify-id',
  templateUrl: './agent-verify-id.component.html',
  styleUrls: ['./agent-verify-id.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    CaptionedImageComponent,
    ListRetakeRequestsComponent,
    TranslateModule,
    SessionAttendanceStatusComponent
  ]
})
export class AgentVerifyIdComponent implements OnInit, OnDestroy {
  private verificationsService = inject(VerificationsService);
  private retakeRequestsService = inject(RetakeRequestsService);
  private overlayService = inject(OverlayService);
  private router = inject(Router);
  private cable = inject(Cable);

  @Input() session: Session;
  @Input() passed: EventEmitter<boolean>;
  @Input() failed: EventEmitter<string>;
  @Input() retaken: EventEmitter<RetakeDocumentPayload>;

  channel: ActionCable.Channel;

  attacheOptions: AttacheOptions;
  freshFacePhoto: AttachePayload;
  freshIdPhoto: AttachePayload;

  passedSubscription: Subscription;
  failedSubscription: Subscription;
  retakeRequestedSubscription: Subscription;

  get identification(): Identification {
    return this.session.activeIdentification;
  }

  get liveAgent(): boolean {
    return this.session.live && !this.identification;
  }

  get facePhotoUrl(): string | null {
    if (this.freshFacePhoto) {
      return this.attacheOptions.urlFor(this.freshFacePhoto);
    }

    if (this.identification) {
      return this.identification.facePhoto.url;
    }

    return null;
  }

  get idPhotoUrl(): string | null {
    if (this.freshIdPhoto) {
      return this.attacheOptions.urlFor(this.freshIdPhoto);
    }

    if (this.identification) {
      return this.identification.idPhoto.url;
    }

    return null;
  }

  get retakeRequests() {
    return this.session.retakeRequests;
  }

  ngOnInit() {
    this.attacheOptions = new AttacheOptions(this.session.attacheOptions);
    this.passedSubscription = this.passed.asObservable().subscribe(() => this.passSession());
    this.failedSubscription = this.failed.asObservable().subscribe((reason) => this.failSession(reason));
    this.retakeRequestedSubscription = this.retaken
      .asObservable()
      .subscribe((reason: RetakeDocumentPayload) => this.retakeDocumentForSession(reason));

    this.createChannel();
  }

  ngOnDestroy() {
    this.passedSubscription.unsubscribe();
    this.failedSubscription.unsubscribe();
    this.retakeRequestedSubscription.unsubscribe();
    this.channel.unsubscribe();
  }

  goHome() {
    setTimeout(() => this.router.navigateByUrl('/'), 100);
  }

  retakePhoto() {
    this.channel.perform('rebroadcast', { currentStep: 3, nextStep: 6 });
  }

  retakeID() {
    this.channel.perform('rebroadcast', { currentStep: 4, nextStep: 6 });
  }

  private createChannel() {
    const cableParams = {
      channel: 'SessionChannel',
      token: this.session.token
    };
    this.channel = this.cable.create(cableParams, (data: SessionChannelData) => {
      if (data.freshFacePhoto) {
        this.freshFacePhoto = data.freshFacePhoto;
      }

      if (data.freshIdPhoto) {
        this.freshIdPhoto = data.freshIdPhoto;
      }
    });

    this.channel.perform('rebroadcast', { requestPhotos: true });
  }

  private passSession() {
    this.verificationsService.pass(this.session.id, VerificationServiceEnum.Identification).subscribe({
      next: (verification) => {
        const { finished, services } = verification.session;
        const automatedAttendance = services.includes(SessionServicesEnum.AutomatedAttendance);

        if (finished || automatedAttendance) {
          this.goHome();
        }
      },
      error: (error) => console.error('error passing session', error)
    });
  }

  private retakeDocumentForSession(reason: RetakeDocumentPayload) {
    this.retakeRequestsService
      .create({
        sessionId: this.session.id,
        retakeRequest: {
          facePhoto: reason.facePhoto,
          idPhoto: reason.idPhoto,
          facePhotoReason: reason.facePhotoReason,
          idPhotoReason: reason.idPhotoReason
        }
      })
      .subscribe({
        next: () => {
          this.goHome();
        },
        error: (error) => console.error('error retaking document', error)
      });
  }

  private failSession(reason: string) {
    this.verificationsService.fail(this.session.id, VerificationServiceEnum.Identification, reason).subscribe({
      next: () => {
        this.overlayService.open(DoneAuditingComponent, {
          session: this.session,
          passOrFail: 'fail'
        });
        this.goHome();
      },
      error: (error) => console.error('error failing session', error)
    });
  }
}
